import React, { useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  XMarkIcon,
  DocumentDuplicateIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";
import { emailService } from "../../services/emailService";
import { useToast } from "../../hooks/useToast";

interface ShareProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: {
    id: string;
    name: string;
    url: string;
    guest_password: string;
  };
}

export const ShareProjectModal: React.FC<ShareProjectModalProps> = ({
  isOpen,
  onClose,
  project,
}) => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const { showToast } = useToast();

  const getShareableLink = () => {
    return `${project.url}?mode=widget&pwd=${project.guest_password}`;
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareableLink());
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
      showToast("Link copied to clipboard!", "success");
    } catch (err) {
      showToast("Failed to copy link", "error");
    }
  };

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    try {
      console.log("Sending invitation email...", {
        email,
        projectName: project.name,
        projectUrl: getShareableLink(),
        guestPassword: project.guest_password,
      });

      await emailService.sendInvitation(
        email,
        project.name,
        getShareableLink(),
        "Project Owner",
        project.guest_password
      );

      showToast("Invitation sent successfully!", "success");
      setEmail(""); // Clear the email input
      onClose();
    } catch (error) {
      console.error("Error sending invitation:", error);
      showToast(
        error instanceof Error ? error.message : "Failed to send invitation",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all">
                <div className="flex justify-between items-center mb-4">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    Share Project
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Link
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={getShareableLink()}
                      readOnly
                      className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-500"
                    />
                    <button
                      onClick={handleCopyLink}
                      className="flex items-center gap-1 px-3 py-2 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      <DocumentDuplicateIcon className="w-4 h-4" />
                      <span className="text-sm">
                        {isCopied ? "Copied!" : "Copy Link"}
                      </span>
                    </button>
                  </div>
                </div>

                <form onSubmit={handleSendEmail} className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Invite by Email
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter email address"
                      className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
                      required
                    />
                    <button
                      type="submit"
                      disabled={isLoading || !email}
                      className="flex items-center gap-1 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-900 disabled:opacity-50"
                    >
                      <UserPlusIcon className="w-4 h-4" />
                      <span className="text-sm">
                        {isLoading ? "Sending..." : "Send Invite"}
                      </span>
                    </button>
                  </div>
                </form>

                <div className="mt-6 flex justify-between">
                  <button
                    onClick={handleCopyLink}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 flex items-center gap-2"
                  >
                    <DocumentDuplicateIcon className="w-4 h-4" />
                    {isCopied ? "Copied!" : "Copy Link"}
                  </button>
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-white bg-black rounded-md hover:bg-gray-900"
                  >
                    Done
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
